import React, { useState } from 'react';
import { FiGithub, FiLinkedin, FiCodepen, FiMusic, FiPlay, FiPause, FiSkipForward } from 'react-icons/fi';
import { useLofiStore, LOFI_STATIONS } from '../../store/lofiStore';
import './RoomScene.css';

const SOCIALS = [
  { label: 'GitHub', href: 'https://github.com/IIBladeII', icon: FiGithub },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/pedro-riccio/', icon: FiLinkedin },
  { label: 'Code', href: 'https://github.com/IIBladeII?tab=repositories', icon: FiCodepen },
];

export default function RoomScene({ onEnterOS, initialEntrance = true }) {
  const [isLoading, setIsLoading] = useState(false);
  const { isPlaying, togglePlay, stationIndex, nextStation } = useLofiStore();
  const currentStation = LOFI_STATIONS[stationIndex] || LOFI_STATIONS[0];

  const handleMonitorClick = () => {
    if (isLoading) return;
    setIsLoading(true);
    setTimeout(() => {
      onEnterOS();
    }, 750);
  };

  return (
    <div className={`blade-room-container relative w-full h-full overflow-hidden bg-black select-none ${isLoading ? 'zooming-in' : ''} ${!initialEntrance ? 'no-entrance-fx' : ''}`}>
      {/* 0. EFEITO VINTAGE CRT TV TURN-ON (APENAS NA ENTRADA INICIAL DO SITE) */}
      {initialEntrance && (
        <>
          <div className="crt-phosphor-beam pointer-events-none" />
          <div className="crt-glow-overlay pointer-events-none" />
        </>
      )}
      <div className="scanlines-overlay pointer-events-none" />

      {/* WRAPPER QUE ABRE DO CENTRO NA HORIZONTAL E DEPOIS NA VERTICAL */}
      <div className="room-crt-screen w-full h-full relative">
        {/* 1. BACKGROUND GIF — BLADE'S COZY PIXEL ART ROOM */}
        <img
          src="/blade-room.gif"
          alt="Blade's Cyberpunk Developer Room"
          className="room-bg-image absolute inset-0 w-full h-full object-cover z-0 select-none pointer-events-none"
        />

        {/* OVERLAY DE ILUMINAÇÃO AMBIENTAL SUTIL */}
        <div className="room-ambient-glow absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none z-10" />

        {/* CABEÇALHO CLICÁVEL (CANTO SUPERIOR ESQUERDO) */}
        <div
          onClick={handleMonitorClick}
          className="room-header-left absolute top-5 left-7 z-20 flex items-center gap-3 select-none cursor-pointer opacity-85 hover:opacity-100 transition-all hover:translate-x-1"
          title="Iniciar PC"
        >
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--neon-cyan)] animate-pulse shadow-[0_0_10px_var(--neon-cyan)]" />
          <span className="font-mono text-xs sm:text-sm text-[var(--neon-cyan)] hover:text-white tracking-[0.2em] sm:tracking-[0.25em] font-bold uppercase drop-shadow-[0_0_10px_rgba(0,240,255,0.5)] transition-colors">
            PEDRO RICCIO /// SOFTWARE ENGINEER
          </span>
        </div>

        {/* LINKS SOCIAIS NO CANTO SUPERIOR DIREITO */}
        <div className="room-socials-right absolute top-5 right-7 z-20 flex flex-col gap-2.5 select-none">
          {SOCIALS.map(({ label, href, icon: Icon }, idx) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noreferrer"
              style={{ animationDelay: `${700 + idx * 120}ms` }}
              className="room-social-item flex items-center gap-2.5 font-mono text-[13px] font-semibold text-gray-200 hover:text-white transition-all opacity-90 hover:opacity-100 hover:translate-x-[-3px] drop-shadow-[1px_1px_4px_rgba(0,0,0,0.95)]"
              aria-label={label}
              title={label}
            >
              <Icon size={20} className="text-[var(--neon-cyan)] drop-shadow-[0_0_6px_var(--neon-cyan)]" />
              <span>{label}</span>
            </a>
          ))}
        </div>

        {/* 2. APENAS A SETA ACIMA DA TELA APONTANDO PARA O MONITOR */}
        <div
          onClick={handleMonitorClick}
          className="room-pointer-arrow absolute z-30 cursor-pointer select-none"
          style={{
            top: '31.5%',
            left: '47.7%',
            transform: 'translateX(-50%)',
          }}
          title="Acessar sistema"
        >
          <span className="inline-block text-[var(--neon-cyan)] text-base font-bold animate-bounce drop-shadow-[0_0_12px_var(--neon-cyan)]">
            ▼
          </span>
        </div>

        {/* 3. ÁREA INTERATIVA DO MONITOR — TOTALMENTE TRANSPARENTE COM LOADING DENTRO */}
        <div
          onClick={handleMonitorClick}
          className="monitor-hotspot absolute z-30 cursor-pointer rounded-md transition-all duration-300 group"
          style={{
            top: '35.5%',
            left: '37.1%',
            width: '21.2%',
            height: '20.6%',
          }}
          title="Clique na tela para iniciar o Blade OS"
        >
          {/* Totalmente transparente por padrão, com feedback sutil apenas no hover */}
          <div className="absolute inset-0 rounded-md border border-[var(--neon-cyan)]/0 group-hover:border-[var(--neon-cyan)]/40 group-hover:bg-[var(--neon-cyan)]/10 transition-all duration-300" />

          {/* LOADING INDICATOR DENTRO DA TELA DO PC */}
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 rounded-sm pointer-events-none z-40">
              <div className="w-5 h-5 rounded-full border-2 border-cyan-500/20 border-t-[var(--neon-cyan)] animate-spin shadow-[0_0_8px_#00f0ff]" />
              <span className="mt-1.5 font-mono text-[9px] font-bold tracking-widest text-[var(--neon-cyan)] uppercase animate-pulse">
                LOADING...
              </span>
            </div>
          )}
        </div>

        {/* 4. BOTÃO START — APENAS TEXTO BRILHANTE */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center justify-center">
          <button
            onClick={handleMonitorClick}
            className="start-system-btn group bg-transparent border-0 p-0 text-[var(--neon-cyan)] hover:text-white font-mono text-base sm:text-lg font-bold tracking-[0.4em] uppercase cursor-pointer transition-all duration-300 hover:scale-110 active:scale-95"
            title="START"
          >
            <span className="start-btn-text">
              {isLoading ? 'LOADING...' : 'START'}
            </span>
          </button>
        </div>

        {/* 5. CONTROLES DE LOFI NO CANTO INFERIOR ESQUERDO */}
        <div className="room-lofi-widget absolute bottom-7 left-7 z-30 flex items-center gap-3 select-none">
          <button
            type="button"
            onClick={togglePlay}
            className="room-lofi-play-btn flex items-center justify-center w-10 h-10 rounded-full bg-black/60 border border-[var(--neon-cyan)]/40 hover:border-[var(--neon-cyan)] hover:bg-[var(--neon-cyan)]/15 text-[var(--neon-cyan)] hover:text-white transition-all shadow-[0_0_12px_rgba(0,240,255,0.25)] hover:shadow-[0_0_20px_rgba(0,240,255,0.6)] cursor-pointer active:scale-95"
            title={isPlaying ? 'Pausar Lofi' : 'Tocar Lofi'}
            aria-label={isPlaying ? 'Pausar Lofi' : 'Tocar Lofi'}
          >
            {isPlaying ? <FiPause size={15} /> : <FiPlay size={15} className="ml-0.5" />}
          </button>

          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-gray-200 tracking-wider flex items-center gap-1.5 drop-shadow-[0_0_8px_rgba(0,240,255,0.3)]">
                <FiMusic size={13} className="text-[var(--neon-cyan)]" />
                <span>LOFI.FM</span>
              </span>
              {isPlaying && (
                <div className="flex items-end gap-0.5 h-3">
                  <span className="w-0.5 bg-[var(--neon-cyan)] animate-[pulse_0.8s_infinite] h-full" />
                  <span className="w-0.5 bg-[var(--neon-cyan)] animate-[pulse_0.6s_infinite_0.2s] h-2/3" />
                  <span className="w-0.5 bg-[var(--neon-cyan)] animate-[pulse_0.9s_infinite_0.4s] h-4/5" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono text-[10px] text-gray-400 truncate max-w-[130px] sm:max-w-[180px]">
                {currentStation.name}
              </span>
              <button
                type="button"
                onClick={nextStation}
                className="p-1 text-gray-400 hover:text-[var(--neon-cyan)] transition-colors cursor-pointer"
                title="Próxima rádio"
                aria-label="Próxima rádio"
              >
                <FiSkipForward size={12} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
